import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Edit,
  Trash2,
  Users,
  TrendingUp,
  Clock,
  Settings,
} from 'lucide-react';
import { usePatientEnrollers } from '@/hooks/pa/usePatientEnrollers.ts';
import { useAuth } from '@/contexts/AuthContext';
import { useBackButtonLogout } from '@/hooks/ea/useBackButtonLogout.ts';
import LogoutConfirmDialog from '@/components/common/LogoutConfirmDialog.tsx';
import EditPatientEnrollerPESDialog from '@/components/pa/EditPatientEnrollerPESDialog.tsx';
import DeletePatientEnrollerDialog from '@/components/pa/DeletePatientEnrollerDialog.tsx';
import AddPatientEnrollerDialog from '@/components/pa/AddPatientEnrollerDialog.tsx';
import UnifiedHeader from '@/components/common/UnifiedHeader';
import PaginationComponent from '@/components/common/PaginationComponent';
import PASearchForm from '@/components/pa/PASearchForm';
import { usePADashboardState } from '@/hooks/pa/usePADashboardState';
import { usePADashboardOperations } from '@/hooks/pa/usePADashboardOperations';

const PADashboard = () => {
  const { patientEnrollers, isLoading, refetch } = usePatientEnrollers();
  const { user } = useAuth();
  
  // Use the new state and operations hooks
  const { state, actions } = usePADashboardState(patientEnrollers);
  const operations = usePADashboardOperations(actions, refetch);

  // State to track expanded clinic rows
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(new Set());

  // Handle browser back button to show logout dialog
  useBackButtonLogout({
    onLogoutRequest: () => actions.setShowLogoutDialog(true)
  });

  // Get filtered data and pagination using operations
  const filteredPatientEnrollers = operations.getFilteredPatientEnrollers(patientEnrollers, state.searchTerm);
  const paginationData = operations.getPaginationData(filteredPatientEnrollers, state.currentPage, state.recordsPerPage);
  const stats = operations.getDashboardStats(patientEnrollers);

  // Reset to first page when search term changes
  React.useEffect(() => {
    actions.setCurrentPage(1);
  }, [state.searchTerm]);

  // Function to toggle expanded state for a row
  const toggleExpandRow = (enrollerId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(enrollerId)) {
        newSet.delete(enrollerId);
      } else {
        newSet.add(enrollerId);
      }
      return newSet;
    });
  };

  // Function to render clinic names with expand/collapse functionality
  const renderClinicNames = (clinicNames: string, enrollerId: number) => {
    if (!clinicNames || clinicNames === 'No Clinics Assigned') {
      return <Badge variant="secondary" className="text-xs" data-testid={`pa-enroller-no-clinics-${enrollerId}`}>No Clinics Assigned</Badge>;
    }

    const clinics = clinicNames.split(', ').map(name => name.trim());
    const isExpanded = expandedRows.has(enrollerId);

    if (clinics.length <= 4) {
      return (
        <div className="flex flex-wrap gap-1" data-testid={`pa-enroller-clinics-list-${enrollerId}`}>
          {clinics.map((clinic, index) => (
            <Badge key={index} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200" data-testid={`pa-enroller-clinic-badge-${enrollerId}-${index}`}>
              {clinic}
            </Badge>
          ))}
        </div>
      );
    }

    const displayedClinics = isExpanded ? clinics : clinics.slice(0, 4);
    const remainingCount = clinics.length - 4;

    return (
      <div className="flex flex-wrap gap-1" data-testid={`pa-enroller-clinics-list-${enrollerId}`}>
        {displayedClinics.map((clinic, index) => (
          <Badge key={index} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200" data-testid={`pa-enroller-clinic-badge-${enrollerId}-${index}`}>
            {clinic}
          </Badge>
        ))}
        {!isExpanded && (
          <Badge 
            variant="outline" 
            className="text-xs bg-gray-100 text-gray-700 border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors"
            onClick={() => toggleExpandRow(enrollerId)}
            data-testid={`pa-enroller-clinic-expand-${enrollerId}`}
          >
            +{remainingCount} more
          </Badge>
        )}
        {isExpanded && (
          <Badge 
            variant="outline" 
            className="text-xs bg-gray-100 text-gray-700 border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors"
            onClick={() => toggleExpandRow(enrollerId)}
            data-testid={`pa-enroller-clinic-collapse-${enrollerId}`}
          >
            Show less
          </Badge>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" id="pa-dashboard" data-testid="pa-dashboard">
      {/* Main Content */}
      <main className="p-8 max-w-7xl mx-auto" data-testid="pa-dashboard-content">
        <div className="space-y-6" data-testid="pa-dashboard-sections">

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mb-6" data-testid="pa-dashboard-action-buttons-section">
            <Button
              id="manage-clinic-slots-button"
              name="manageClinicSlots"
              data-testid="pa-manage-clinic-slots-button"
              onClick={operations.handleManageClinicSlots}
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Settings className="h-5 w-5 mr-2" />
              Manage Clinic Appointment Slots
            </Button>
          </div>

          {/* Modern Stats Cards with gradient design */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-testid="pa-dashboard-stats-cards">
            {/* Total Enrollers Card - Blue theme */}
            <Card className="bg-gradient-to-br from-blue-400 via-blue-500 to-cyan-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-2xl overflow-hidden" data-testid="pa-dashboard-total-card">
              <CardContent className="p-8 relative" data-testid="pa-dashboard-total-content">
                <div className="absolute top-4 right-4" data-testid="pa-dashboard-total-icon-wrapper">
                  <Users className="h-8 w-8 text-white/70" data-testid="pa-dashboard-total-icon" />
                </div>
                <div className="flex flex-col" data-testid="pa-dashboard-total-data">
                  <div className="text-4xl font-bold mb-2 tracking-tight" data-testid="pa-dashboard-total-count">{stats.total}</div>
                  <p className="text-white/90 font-semibold text-lg mb-1" data-testid="pa-dashboard-total-label">Total Enrollers</p>
                  <p className="text-white/70 text-sm" data-testid="pa-dashboard-total-description">All enrollment specialists</p>
                </div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mb-8" data-testid="pa-dashboard-total-decoration" />
              </CardContent>
            </Card>

            {/* Active Card - Emerald theme */}
            <Card className="bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-2xl overflow-hidden" data-testid="pa-dashboard-active-card">
              <CardContent className="p-8 relative" data-testid="pa-dashboard-active-content">
                <div className="absolute top-4 right-4" data-testid="pa-dashboard-active-icon-wrapper">
                  <TrendingUp className="h-8 w-8 text-white/70" data-testid="pa-dashboard-active-icon" />
                </div>
                <div className="flex flex-col" data-testid="pa-dashboard-active-data">
                  <div className="text-4xl font-bold mb-2 tracking-tight" data-testid="pa-dashboard-active-count">{stats.active}</div>
                  <p className="text-white/90 font-semibold text-lg mb-1" data-testid="pa-dashboard-active-label">Active</p>
                  <p className="text-white/70 text-sm" data-testid="pa-dashboard-active-description">Currently active</p>
                </div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mb-8" data-testid="pa-dashboard-active-decoration" />
              </CardContent>
            </Card>

            {/* Deferred Card - Orange theme */}
            <Card className="bg-gradient-to-br from-amber-400 via-orange-400 to-red-400 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-2xl overflow-hidden" data-testid="pa-dashboard-deferred-card">
              <CardContent className="p-8 relative" data-testid="pa-dashboard-deferred-content">
                <div className="absolute top-4 right-4" data-testid="pa-dashboard-deferred-icon-wrapper">
                  <Clock className="h-8 w-8 text-white/70" data-testid="pa-dashboard-deferred-icon" />
                </div>
                <div className="flex flex-col" data-testid="pa-dashboard-deferred-data">
                  <div className="text-4xl font-bold mb-2 tracking-tight" data-testid="pa-dashboard-deferred-count">{stats.deferred}</div>
                  <p className="text-white/90 font-semibold text-lg mb-1" data-testid="pa-dashboard-deferred-label">Deferred</p>
                  <p className="text-white/70 text-sm" data-testid="pa-dashboard-deferred-description">Pending tasks</p>
                </div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mb-8" data-testid="pa-dashboard-deferred-decoration" />
              </CardContent>
            </Card>

            {/* Follow Up Card - Purple theme */}
            <Card className="bg-gradient-to-br from-purple-400 via-purple-500 to-pink-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-2xl overflow-hidden" data-testid="pa-dashboard-followup-card">
              <CardContent className="p-8 relative" data-testid="pa-dashboard-followup-content">
                <div className="absolute top-4 right-4" data-testid="pa-dashboard-followup-icon-wrapper">
                  <Settings className="h-8 w-8 text-white/70" data-testid="pa-dashboard-followup-icon" />
                </div>
                <div className="flex flex-col" data-testid="pa-dashboard-followup-data">
                  <div className="text-4xl font-bold mb-2 tracking-tight" data-testid="pa-dashboard-followup-count">{stats.followUp}</div>
                  <p className="text-white/90 font-semibold text-lg mb-1" data-testid="pa-dashboard-followup-label">Follow Up</p>
                  <p className="text-white/70 text-sm" data-testid="pa-dashboard-followup-description">Requires follow up</p>
                </div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mb-8" data-testid="pa-dashboard-followup-decoration" />
              </CardContent>
            </Card>
          </div>

          {/* Search Bar - Above Table */}
          <div className="mb-6" data-testid="pa-dashboard-search-section">
            <PASearchForm
              searchTerm={state.searchTerm}
              onSearchTermChange={actions.setSearchTerm}
              onSearch={operations.handleSearch}
              onReload={operations.handleSearchReload}
              isLoading={state.isSearching}
              data-testid="pa-search-form"
            />
          </div>

          {/* Patient Enrollers Table */}
          <Card className="bg-white shadow-sm" data-testid="pa-enrollers-table-card">
            <CardContent className="p-0" data-testid="pa-enrollers-table-content">
              {isLoading ? (
                <div className="flex items-center justify-center h-64" data-testid="pa-dashboard-loading">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" data-testid="pa-dashboard-spinner"></div>
                </div>
              ) : filteredPatientEnrollers.length === 0 ? (
                <div className="text-center py-12" data-testid="pa-dashboard-empty-state">
                  <p className="text-gray-500 text-lg" data-testid="pa-dashboard-empty-message">
                    {state.searchTerm ? `No patient enrollment specialists found matching "${state.searchTerm}"` : 'No patient enrollment specialists found'}
                  </p>
                </div>
              ) : (
                <>
                  <Table data-testid="pa-enrollers-table">
                    <TableHeader data-testid="pa-enrollers-table-header">
                      <TableRow data-testid="pa-enrollers-header-row">
                        <TableHead data-testid="pa-table-header-name">Full Name</TableHead>
                        <TableHead data-testid="pa-table-header-email">Email</TableHead>
                        <TableHead data-testid="pa-table-header-clinics">Clinic Names</TableHead>
                        <TableHead data-testid="pa-table-header-tasks">Assigned Task(s)</TableHead>
                        <TableHead data-testid="pa-table-header-actions">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="pa-enrollers-table-body">
                      {paginationData.currentRecords.map((pe) => (
                        <TableRow key={pe.patient_enroller_id} data-testid={`pa-enroller-row-${pe.patient_enroller_id}`}>
                          <TableCell className="font-medium" data-testid={`pa-enroller-name-${pe.patient_enroller_id}`}>{pe.full_name}</TableCell>
                          <TableCell data-testid={`pa-enroller-email-${pe.patient_enroller_id}`}>{pe.email}</TableCell>
                          <TableCell data-testid={`pa-enroller-clinics-${pe.patient_enroller_id}`}>
                            {renderClinicNames(pe.clinic_names, pe.patient_enroller_id)}
                          </TableCell>
                          <TableCell data-testid={`pa-enroller-tasks-${pe.patient_enroller_id}`}>
                            {pe.deferred_task || pe.follow_up ? (
                              <div className="flex items-center gap-1 flex-nowrap" data-testid={`pa-enroller-tasks-list-${pe.patient_enroller_id}`}>
                                {pe.deferred_task && <Badge variant="secondary" className="text-xs whitespace-nowrap" data-testid={`pa-enroller-deferred-badge-${pe.patient_enroller_id}`}>Deferred</Badge>}
                                {pe.follow_up && <Badge variant="default" className="text-xs whitespace-nowrap" data-testid={`pa-enroller-followup-badge-${pe.patient_enroller_id}`}>Follow up</Badge>}
                              </div>
                            ) : (
                              <Badge variant="secondary" className="text-xs" data-testid={`pa-enroller-no-task-badge-${pe.patient_enroller_id}`}>No Task Assigned</Badge>
                            )}
                          </TableCell>
                          <TableCell data-testid={`pa-enroller-actions-${pe.patient_enroller_id}`}>
                            <div className="flex space-x-2" data-testid={`pa-enroller-actions-wrapper-${pe.patient_enroller_id}`}>
                              <Button
                                id={`edit-enroller-${pe.patient_enroller_id}`}
                                name={`editEnroller${pe.patient_enroller_id}`}
                                data-testid={`pa-enroller-edit-button-${pe.patient_enroller_id}`}
                                variant="outline"
                                aria-label={`Edit enroller ${pe.full_name}`}
                                size="icon"
                                onClick={() => operations.handleEditEnroller(pe)}
                                className="h-8 w-8"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                id={`delete-enroller-${pe.patient_enroller_id}`}
                                name={`deleteEnroller${pe.patient_enroller_id}`}
                                data-testid={`pa-enroller-delete-button-${pe.patient_enroller_id}`}
                                variant="outline"
                                aria-label={`Delete enroller ${pe.full_name}`}
                                size="icon"
                                onClick={() => operations.handleDeleteEnroller(pe)}
                                className="h-8 w-8 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Common Pagination Component */}
                  <div className="pagination-container" data-testid="pa-enrollers-pagination-container">
                    <PaginationComponent
                      currentPage={state.currentPage}
                      totalPages={paginationData.totalPages}
                      recordsPerPage={state.recordsPerPage}
                      totalRecords={paginationData.totalRecords}
                      startIndex={paginationData.startIndex}
                      endIndex={paginationData.endIndex}
                      onPageChange={operations.handlePageChange}
                      onRecordsPerPageChange={operations.handleRecordsPerPageChange}
                      data-testid="pa-enrollers-pagination"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <EditPatientEnrollerPESDialog
        open={state.showEditDialog}
        onOpenChange={actions.setShowEditDialog}
        patientEnroller={state.editingEnroller}
        onSave={operations.handleSaveEnroller}
        data-testid="pa-edit-enroller-dialog"
      />

      <DeletePatientEnrollerDialog
        open={state.showDeleteDialog}
        onOpenChange={actions.setShowDeleteDialog}
        patientEnroller={state.deletingEnroller}
        onDelete={operations.handleConfirmDelete}
        data-testid="pa-delete-enroller-dialog"
      />

      <AddPatientEnrollerDialog
        open={state.showAddDialog}
        onOpenChange={actions.setShowAddDialog}
        onSuccess={operations.handleAddSuccess}
        data-testid="pa-add-enroller-dialog"
      />

      <LogoutConfirmDialog
        open={state.showLogoutDialog}
        onOpenChange={actions.setShowLogoutDialog}
        onConfirm={operations.handleLogout}
        data-testid="pa-logout-confirm-dialog"
      />
    </div>
  );
};

export default PADashboard;
